import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Main from '../../../../BrowserSupport/Main';
import { newGroupStore } from '../../../../NewGroupStore';
import I18nTitle from '../../../../Title/I18nTitle';
import { T } from '../../../../Translations';
import MainNav from '../../../Platform/MainNav';
import GroupCollectionsSection from '../Shared/GroupCollectionsSection';
import GroupLicenseSelectionSection from '../Shared/GroupLicenseSelectionSection';
import GroupAdded from './GroupAdded';
import GroupMetadataSection from '../Shared/GroupMetadataSection';

/**
 * Add Group full-page workflow.
 *
 * Mirrors the Add Participant page layout: header strip with title +
 * inline action buttons, scrollable content region with section cards,
 * sticky footer with duplicate action buttons.
 *
 * State (form fields, license-picker UI, selected collections) lives in
 * the module-level `newGroupStore` singleton so a submit-and-add-another
 * flow can survive the SuccessModal without unmounting.
 */
function NewGroupPage() {
  const navigate = useNavigate();

  useEffect(() => {
    newGroupStore.load();
    return () => newGroupStore.clear();
  }, []);

  const handleCancel = () => {
    navigate('/people/groups');
  };

  const state = newGroupStore.state;

  const primaryButton = (
    <button
      className="px-6 py-2.5 bg-accent text-accent-foreground rounded-md transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
      style={{ fontWeight: 'var(--font-weight-medium)' }}
      disabled={!newGroupStore.canSubmit || newGroupStore.isSubmitting}
      onClick={newGroupStore.submit}
      data-intercom-target="add-group-submit-button"
    >
      {newGroupStore.isSubmitting ? <T kind="Creating..." /> : <T kind="Create Group" />}
    </button>
  );

  const cancelButton = (
    <button
      className="px-4 py-2 text-primary hover:opacity-80 transition-opacity"
      style={{ fontWeight: 'var(--font-weight-medium)' }}
      onClick={handleCancel}
      data-intercom-target="add-group-cancel-button"
    >
      <T kind="Cancel" />
    </button>
  );

  return (
    <>
      <I18nTitle title="Add Group" />
      <div className="min-h-screen flex flex-col">
        <MainNav />
        <div className="border-b border-border bg-background py-6">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-3xl" style={{ fontWeight: 600 }}>
                  <T kind="Add Group" />
                </h1>
                <p className="text-muted-foreground">
                  <T kind="Groups let you assign licenses and collections to many people at once." />
                </p>
              </div>
              <div className="flex items-center gap-3">
                {cancelButton}
                {primaryButton}
              </div>
            </div>
          </div>
        </div>
        <div className="flex-grow bg-muted/30">
          <div className="max-w-5xl mx-auto px-12 py-8">
            <Main id="main">
              {state.kind === 'loading' || state.kind === 'waiting' ? (
                <div className="p-12 text-center text-muted-foreground">
                  <T kind="Loading..." />
                </div>
              ) : state.kind === 'load-error' ? (
                <div className="p-12 text-center text-destructive">
                  <T kind="Could not load new-group defaults. Please refresh." />
                </div>
              ) : (
                <div className="space-y-6">
                  {state.kind === 'submit-error' && (
                    <div className="border border-destructive/40 bg-destructive/10 text-destructive rounded-md p-4">
                      <T kind="Could not create the group. Please check your entries and try again." />
                    </div>
                  )}

                  <GroupMetadataSection
                    name={newGroupStore.name}
                    description={newGroupStore.description}
                    active={newGroupStore.active}
                    onNameChange={newGroupStore.setName}
                    onDescriptionChange={newGroupStore.setDescription}
                    onActiveChange={newGroupStore.setActive}
                    sectionNumber={1}
                  />

                  {/* Sections stay enabled during `submitting` — the primary
                   * button's own disabled state prevents a second POST, and
                   * the POST window is short enough (<1s) that field edits
                   * after submit are a non-issue. */}
                  <GroupLicenseSelectionSection
                    licenses={newGroupStore.licenses}
                    selectedLicenseIds={newGroupStore.selectedLicenseIds}
                    onSelectedLicenseIdsChange={newGroupStore.setSelectedLicenseIds}
                    licenseSearchQuery={newGroupStore.licenseSearchQuery}
                    onLicenseSearchQueryChange={newGroupStore.setLicenseSearchQuery}
                    expandedLicenseTypes={newGroupStore.expandedLicenseTypes}
                    onToggleLicenseTypeExpanded={newGroupStore.toggleLicenseTypeExpanded}
                    sectionNumber={2}
                  />

                  <GroupCollectionsSection
                    collections={newGroupStore.collections}
                    selectedCollectionIds={newGroupStore.selectedCollectionIds}
                    onSelectedCollectionIdsChange={newGroupStore.setSelectedCollectionIds}
                    sectionNumber={3}
                  />
                </div>
              )}
            </Main>
          </div>
        </div>
        <div className="border-t border-border bg-card py-4">
          <div className="max-w-5xl mx-auto px-12">
            <div className="flex items-center justify-between">
              {cancelButton}
              {primaryButton}
            </div>
          </div>
        </div>
      </div>

      <GroupAdded />
    </>
  );
}

export default observer(NewGroupPage);
